import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { AdminCardRecord } from '../../services/api';
import apiService from '../../services/api';

export interface CardsState {
    requestedCards: AdminCardRecord[];
    fuelCards: any[];
    isLoading: boolean;
    error: string | null;
    servicePingStatus: 'idle' | 'success' | 'failed' | 'loading';
}

const initialState: CardsState = {
    requestedCards: [],
    fuelCards: [],
    isLoading: false,
    error: null,
    servicePingStatus: 'idle',
};

export const fetchRequestedCards = createAsyncThunk(
    'cards/fetchRequested',
    async (_, { rejectWithValue }) => {
        try {
            const cards = await apiService.getRequestedCards();
            return cards;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch requested cards');
        }
    }
);

export const fetchFuelCards = createAsyncThunk(
    'cards/fetchFuelCards',
    async (_, { rejectWithValue }) => {
        try {
            const cards = await apiService.getFuelCards();
            return cards;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch fuel cards');
        }
    }
);

export const requestCard = createAsyncThunk(
    'cards/requestCard',
    async (data: { companyId: string; printDetails?: any }, { rejectWithValue }) => {
        try {
            const newCard = await apiService.requestFuelCard(data);
            return newCard;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to request new card');
        }
    }
);

export const createCardForClient = createAsyncThunk(
    'cards/createForClient',
    async (data: {
        companyId: string;
        cid: string;
        contractId?: string;
    }, { rejectWithValue }) => {
        try {
            const newCard = await apiService.createCardForClient(data);
            return newCard;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create card for client');
        }
    }
);

export const bulkImportCards = createAsyncThunk(
    'cards/bulkImport',
    async (data: { file: File; companyId: string }, { rejectWithValue }) => {
        try {
            const result = await apiService.importCardsCsv(data.file, data.companyId);
            return result;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to import cards from CSV');
        }
    }
);

export const checkCardStatus = createAsyncThunk(
    'cards/checkStatus',
    async (id: string, { rejectWithValue }) => {
        try {
            const statusData = await apiService.checkCardStatus(id);
            return statusData;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to check card status');
        }
    }
);

export const issueCard = createAsyncThunk(
    'cards/issue',
    async (payload: { id: string; accountId?: string; contractId?: string }, { rejectWithValue }) => {
        try {
            const card = await apiService.issueCard(payload.id, {
                accountId: payload.accountId,
                contractId: payload.contractId
            });
            return card;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to issue card');
        }
    }
);

export const pingOnafriq = createAsyncThunk(
    'cards/pingService',
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiService.pingOnafriq();
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Payment provider connectivity test failed');
        }
    }
);

export const toggleCardStatus = createAsyncThunk(
    'cards/toggleStatus',
    async ({ cardReference, action }: { cardReference: string; action: 'LOCK' | 'UNLOCK' }, { rejectWithValue }) => {
        try {
            const result = await apiService.toggleCardStatus(cardReference, action);
            return { cardReference, action, result };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update card status');
        }
    }
);

export const loadCardFunds = createAsyncThunk(
    'cards/loadFunds',
    async ({ cardReference, amount }: { cardReference: string; amount: number }, { rejectWithValue }) => {
        try {
            const result = await apiService.loadCardFunds(cardReference, amount);
            return { cardReference, amount, result };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load funds to card');
        }
    }
);

export const fetchCardTransactions = createAsyncThunk(
    'cards/fetchTransactions',
    async (cardReference: string, { rejectWithValue }) => {
        try {
            const transactions = await apiService.getCardTransactions(cardReference);
            return transactions;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch card transactions');
        }
    }
);

export const editCard = createAsyncThunk(
    'cards/editCard',
    async (
        { cardId, updates }: { cardId: string; updates: { companyId?: string; status?: string; vehicleId?: string | null } },
        { rejectWithValue }
    ) => {
        try {
            const updated = await apiService.updateCard(cardId, updates);
            return updated;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update card');
        }
    }
);

export const deleteCard = createAsyncThunk(
    'cards/deleteCard',
    async (cardId: string, { rejectWithValue }) => {
        try {
            const result = await apiService.deleteCard(cardId);
            return { cardId, ...result };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete card');
        }
    }
);

const cardsSlice = createSlice({
    name: 'cards',
    initialState,
    reducers: {
        clearCardsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRequestedCards.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchRequestedCards.fulfilled, (state, action) => {
                state.isLoading = false;
                state.requestedCards = action.payload;
                state.error = null;
            })
            .addCase(fetchRequestedCards.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Request Card
            .addCase(requestCard.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(requestCard.fulfilled, (state, action) => {
                state.isLoading = false;
                state.requestedCards.push(action.payload);
                state.error = null;
            })
            .addCase(requestCard.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create Card For Client (admin-initiated)
            .addCase(createCardForClient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createCardForClient.fulfilled, (state, action) => {
                state.isLoading = false;
                // importRegisteredCard returns an ACTIVE card entity → belongs in fuelCards
                if (action.payload?.id) {
                    state.fuelCards.unshift(action.payload);
                }
                state.error = null;
            })
            .addCase(createCardForClient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Fuel Cards
            .addCase(fetchFuelCards.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchFuelCards.fulfilled, (state, action) => {
                state.isLoading = false;
                state.fuelCards = action.payload;
                state.error = null;
            })
            .addCase(fetchFuelCards.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Issue Card
            .addCase(issueCard.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(issueCard.fulfilled, (state, action) => {
                state.isLoading = false;
                // Remove from requestedCards once issued
                state.requestedCards = state.requestedCards.filter(c => c.id !== action.payload.id);
                state.error = null;
            })
            .addCase(issueCard.rejected, (state) => {
                state.isLoading = false;
                // Don't set state.error here to avoid covering the whole page
            })
            // Ping Service
            .addCase(pingOnafriq.pending, (state) => {
                state.servicePingStatus = 'loading';
            })
            .addCase(pingOnafriq.fulfilled, (state) => {
                state.servicePingStatus = 'success';
            })
            .addCase(pingOnafriq.rejected, (state) => {
                state.servicePingStatus = 'failed';
            })
            // Toggle Card Status
            .addCase(toggleCardStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(toggleCardStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const { cardReference, action: statusAction } = action.payload;
                const targetCard = state.fuelCards.find(c => c.cardReference === cardReference);
                if (targetCard) {
                    targetCard.status = statusAction === 'LOCK' ? 'LOCKED' : 'ACTIVE';
                }
                state.error = null;
            })
            .addCase(toggleCardStatus.rejected, (state) => {
                state.isLoading = false;
                // Don't set state.error here
            })
            // Load Card Funds
            .addCase(loadCardFunds.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadCardFunds.fulfilled, (state) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(loadCardFunds.rejected, (state) => {
                state.isLoading = false;
                // Don't set state.error here
            })
            // Edit Card (reassign company / change status)
            .addCase(editCard.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(editCard.fulfilled, (state, action) => {
                state.isLoading = false;
                const idx = state.fuelCards.findIndex(c => c.id === action.payload.id);
                if (idx !== -1) {
                    state.fuelCards[idx] = action.payload;
                }
                state.error = null;
            })
            .addCase(editCard.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Bulk Import Cards from CSV
            .addCase(bulkImportCards.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(bulkImportCards.fulfilled, (state) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(bulkImportCards.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete Card
            .addCase(deleteCard.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteCard.fulfilled, (state, action) => {
                state.isLoading = false;
                const deletedId = action.payload.cardId;
                state.fuelCards = state.fuelCards.filter(c => c.id !== deletedId && c.cardReference !== deletedId);
                state.requestedCards = state.requestedCards.filter(c => c.id !== deletedId && c.cardReference !== deletedId);
                state.error = null;
            })
            .addCase(deleteCard.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearCardsError } = cardsSlice.actions;

export const selectRequestedCards = (state: { cards: CardsState }) => state.cards.requestedCards;
export const selectFuelCards = (state: { cards: CardsState }) => state.cards.fuelCards;
export const selectCardsLoading = (state: { cards: CardsState }) => state.cards.isLoading;
export const selectCardsError = (state: { cards: CardsState }) => state.cards.error;
export const selectServicePingStatus = (state: { cards: CardsState }) => state.cards.servicePingStatus;

export default cardsSlice.reducer;
